import { useRef, useState, useEffect, useMemo } from 'react';
import { Box, Link, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AlertCard } from 'common/ui/AlertCard';
import { Form, FormRef, FormOption } from 'common/form';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import {
  SearchableSortDirection,
  SearchableWorkflowSortableFields,
  useSearchWorkflowsLazyQuery,
  useStartInternalSourcingWorkflowRunsMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';
import { buildWorkflowBuilderUrl } from 'modules/layout/utils/buildWorkflowBuilderUrl';
import { addToWorkflowDrawerSx } from 'modules/candidate-profile/styles/addToWorkflowDrawerSx';
import {
  selectWorkflowSchema,
  SelectWorkflowFormData
} from 'modules/candidate-profile/forms/schema/selectWorkflowSchema';
import { getSelectWorkflowFields } from 'modules/candidate-profile/forms/fields/getSelectWorkflowFields';
import { buildInternalSourcingWorkflowFilter } from 'modules/candidate-profile/utils/buildInternalSourcingWorkflowFilter';
import { describeWorkflowPushResult } from 'modules/candidate-profile/utils/describeWorkflowPushResult';
import { toWorkflowOption } from 'modules/candidate-profile/utils/toWorkflowOption';
import { useEnrolledWorkflowCandidates } from 'modules/candidate-profile/hooks/useEnrolledWorkflowCandidates';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';

export type AddToWorkflowCandidate = {
  candidateProfileId: string;
  name: string;
};

type AddToWorkflowDrawerContentProps = {
  candidates: AddToWorkflowCandidate[];
  onAddedToWorkflow?: () => Promise<void>;
};

/** A page of the picker. The search box reaches anything past it. */
const WORKFLOW_OPTIONS_LIMIT = 15;

export const AddToWorkflowDrawerContent: React.FC<
  AddToWorkflowDrawerContentProps
> = ({ candidates, onAddedToWorkflow }) => {
  const formRef = useRef<FormRef<SelectWorkflowFormData>>(null);
  const { closeDrawer } = useDrawerV2();
  const organization = useOrganization();
  const { showToast } = useToast();

  const [workflowOptions, setWorkflowOptions] = useState<FormOption[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );
  const [isAddingToWorkflow, setIsAddingToWorkflow] = useState(false);

  const [searchWorkflows] = useSearchWorkflowsLazyQuery();
  const [startInternalSourcingWorkflowRuns] =
    useStartInternalSourcingWorkflowRunsMutation();

  const selectedCandidateIds = useMemo(
    () => candidates.map((candidate) => candidate.candidateProfileId),
    [candidates]
  );

  const { enrolledIds, notEnrolledIds, isResolved } =
    useEnrolledWorkflowCandidates({
      candidateProfileIds: selectedCandidateIds,
      workflowId: selectedWorkflowId
    });

  useEffect(() => {
    const subscription = formRef.current?.watch(
      (value: SelectWorkflowFormData) => {
        setSelectedWorkflowId(value.workflowId ?? null);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const onSearchFilter = async (query: string) => {
    const { data } = await searchWorkflows({
      variables: {
        filter: buildInternalSourcingWorkflowFilter({
          organizationId: organization.id,
          searchQuery: query
        }),
        sort: [
          {
            field: SearchableWorkflowSortableFields.UpdatedAt,
            direction: SearchableSortDirection.Desc
          }
        ],
        limit: WORKFLOW_OPTIONS_LIMIT
      }
    });

    const options = extractQueryListItems(data).map(toWorkflowOption);

    setWorkflowOptions(options);
    return options;
  };

  const selectedWorkflowLabel = workflowOptions.find(
    (option) => option.value === selectedWorkflowId
  )?.label;

  const handleSubmit = async () => {
    const workflowId = formRef.current?.getValues().workflowId;

    if (!workflowId) return;

    // notEnrolledIds was computed for selectedWorkflowId. If the form has moved on since,
    // refuse rather than push one workflow's candidate list into another — the next render
    // reconciles the two and the recruiter can simply click again.
    if (workflowId !== selectedWorkflowId) return;

    // Until the enrolment lookup lands, an empty notEnrolledIds means "not known yet", not
    // "everyone is already in" — pushing or reporting on it would both be wrong.
    if (!isResolved) return;

    if (notEnrolledIds.length === 0) {
      showToast({
        title: 'Everyone selected is already in this workflow',
        type: ToastType.Info
      });
      return;
    }

    setIsAddingToWorkflow(true);

    try {
      const { data } = await startInternalSourcingWorkflowRuns({
        variables: {
          input: { workflowId, candidateProfileIds: notEnrolledIds }
        }
      });

      const { didEnrol, toast } = describeWorkflowPushResult({
        result: data?.startInternalSourcingWorkflowRuns,
        sent: notEnrolledIds.length,
        workflowLabel:
          typeof selectedWorkflowLabel === 'string'
            ? selectedWorkflowLabel
            : 'workflow',
        workflowUrl: buildWorkflowBuilderUrl(
          workflowBuilderLink,
          organization.id,
          workflowId
        )
      });

      // Nothing enrolled: report it and stay open rather than closing on a success message.
      if (!didEnrol) {
        showToast(toast);
        return;
      }

      await onAddedToWorkflow?.();
      showToast(toast);

      closeDrawer();
    } catch {
      showToast({
        title: 'Could not add these candidates to the workflow',
        message: 'Nobody was added. Try again in a moment.',
        type: ToastType.Error
      });
    } finally {
      setIsAddingToWorkflow(false);
    }
  };

  const openWorkflowBuilder = (workflowId?: string | null) => {
    window.open(
      buildWorkflowBuilderUrl(workflowBuilderLink, organization.id, workflowId),
      '_blank',
      'noopener'
    );
  };

  const manageLink = (
    <Link
      component="button"
      type="button"
      onClick={() => openWorkflowBuilder()}
      sx={addToWorkflowDrawerSx.manageLink}
    >
      Manage your workflows
    </Link>
  );

  const viewWorkflowLink = selectedWorkflowId ? (
    <Link
      component="button"
      type="button"
      onClick={() => openWorkflowBuilder(selectedWorkflowId)}
      sx={addToWorkflowDrawerSx.viewWorkflowLink}
    >
      View workflow
      <OpenInNewIcon sx={addToWorkflowDrawerSx.linkIcon} />
    </Link>
  ) : undefined;

  const enrolledNames = candidates
    .filter((candidate) => enrolledIds.includes(candidate.candidateProfileId))
    .map((candidate) => candidate.name);

  const getEnrolledAlertTitle = (): string => {
    if (notEnrolledIds.length === 0) {
      return enrolledIds.length === 1
        ? 'This candidate is already in this workflow'
        : 'All these candidates are already in this workflow';
    }

    return `${enrolledIds.length} candidate${
      enrolledIds.length === 1 ? ' is' : 's are'
    } already in this workflow`;
  };

  return (
    <Box sx={addToWorkflowDrawerSx.container}>
      <Stack gap="8px">
        <Form
          ref={formRef}
          schema={selectWorkflowSchema}
          fields={getSelectWorkflowFields({
            onSearchFilter,
            manageLink,
            viewWorkflowLink
          })}
          onSubmit={handleSubmit}
          onCancel={closeDrawer}
          submitButtonProps={{
            label: 'Add to workflow',
            loading: isAddingToWorkflow
          }}
          cancelButtonProps={{ label: 'Cancel' }}
        />
      </Stack>

      {enrolledIds.length > 0 && (
        <AlertCard type="info" title={getEnrolledAlertTitle()}>
          These people will not be added:
          <Box component="ul" sx={addToWorkflowDrawerSx.enrolledNames}>
            {enrolledNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </Box>
        </AlertCard>
      )}
    </Box>
  );
};
